import { Settings } from '../common/Settings';
import { Logger } from '@shared/common';
import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getApps, initializeApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import type { FirebaseApp, FirebaseOptions } from 'firebase/app';

/** Flat scalar telemetry parameter value accepted by the GA4 adapter. */
export type TelemetryParamValue = string | number | boolean;

/** Flat scalar telemetry parameter bag accepted by the GA4 adapter. */
export type TelemetryParams = Record<string, TelemetryParamValue>;

/** File-local constants for TelemetryClient. */
const localConstants = {
  logContext: 'TelemetryClient',
  runtimeEnvironmentKey: 'environment',
  runtimeEnvModeKey: 'env_mode',
  runtimeVersionKey: 'version',
  userIDKey: 'user_id',
  maxStringValueLength: 100,
  // Credential-bearing keys are always dropped from analytics URLs: a leaked token/password in a URL must never reach GA4 (a security control, not content filtering).
  credentialKeyTokens: ['password', 'token', 'secret', 'apikey', 'api_key'],
  // Injection-looking value content (markup tags, dangerous schemes) — the ONLY patterns dropped from page_location params. Everything else is preserved verbatim so campaign attribution
  // (utm_*, email_campaign, ad-platform IDs) always survives. Control characters are checked separately by code point (regex control classes trip no-control-regex).
  injectionValuePatterns: [/<\/?[a-z]/i, /(?:javascript|data|vbscript)\s*:/i],
  controlCharCeiling: 32,
  // PII-shaped value patterns (email, SSN, phone, card) - applied only to OUR OWN event params (developer-supplied), never to visitor URL params, so a code slip cannot leak PII into GA4.
  piiValuePatterns: [/@/, /\b\d{3}-\d{2}-\d{4}\b/, /\+?\d[\d\s().-]{7,}\d/, /\b(?:\d[ -]*?){13,16}\b/],
  // Path-segment redaction used when sanitizing page_location URLs.
  urlIDPattern: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  urlNumberPattern: /^\d+$/,
  urlIDPlaceholder: ':id'
} as const;

/** Low-level crash-safe Firebase GA4 telemetry adapter. */
export class TelemetryClient {
  /** Singleton instance. */
  private static instance: TelemetryClient;

  /** Firebase Analytics handle. Null means calls are inert. */
  private analytics: Analytics | null = null;

  /** Hard kill-switch for privileged, non-human, or unsupported contexts. */
  private disabled: boolean = false;

  /** In-flight or completed initialization promise. */
  private initPromise: Promise<void> | null = null;

  /** Events tracked while initialization is still in flight. Flushed once analytics is ready, or dropped when initialization fails/disables. */
  private pendingEvents: Array<{ eventName: string; params: TelemetryParams }> = [];

  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton TelemetryClient instance. */
  public static GetInstance(): TelemetryClient {
    return TelemetryClient.instance ?? (TelemetryClient.instance = new TelemetryClient());
  }

  /**
   * Initialize Firebase Analytics once. Expected off/unsupported paths are non-fatal no-ops.
   *
   * @returns A promise that always resolves.
   */
  public async Init(): Promise<void> {
    if (this.disabled) {
      Logger.Debug(localConstants.logContext, 'Telemetry disabled; skipping analytics initialization.');
      return;
    }
    if (this.analytics) {
      Logger.Debug(localConstants.logContext, 'Telemetry already initialized.');
      return;
    }
    if (!this.initPromise) this.initPromise = this.initInternal();
    await this.initPromise;
    this.flushPendingEvents();
  }

  /** Hard-disable telemetry for privileged or non-human contexts. */
  public Disable(): void {
    this.disabled = true;
    this.analytics = null;
    this.initPromise = null;
    this.pendingEvents = [];
    Logger.Debug(localConstants.logContext, 'Telemetry disabled.');
  }

  /**
   * Sanitize a URL for use as a GA4 `page_location` value. Path segments that look like UUIDs or numeric IDs are redacted, and query parameters are preserved verbatim except credential-bearing
   * keys (token/password/secret/apikey) and values that look like injection attempts (markup, dangerous schemes, control characters) — campaign attribution params (utm_*, email_campaign,
   * ad-platform IDs) always survive. Returns null when the URL cannot be parsed or the runtime has no `window`.
   *
   * @param url - The URL to sanitize.
   * @returns The sanitized URL, or null when it cannot be parsed.
   */
  public SanitizeUrl(url: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const parsed = new URL(url, window.location.href);
      const segments = parsed.pathname
        .split('/')
        .filter(Boolean)
        .map((segment) => (localConstants.urlIDPattern.test(segment) || localConstants.urlNumberPattern.test(segment) ? localConstants.urlIDPlaceholder : segment));
      parsed.pathname = `/${segments.join('/')}`;

      const incomingParams = new URLSearchParams(parsed.search);
      const safeParams = new URLSearchParams();
      for (const [key, value] of incomingParams) {
        if (this.isForbiddenKey(key)) continue;
        if (this.isInjectionValue(value)) continue;
        safeParams.append(key, value);
      }
      parsed.search = safeParams.toString();
      return parsed.toString();
    } catch (err: unknown) {
      Logger.Debug(localConstants.logContext, `SanitizeUrl failed: ${Logger.GetErrorMessage(err)}`);
      return null;
    }
  }

  /**
   * Set the GA4 user id and low-cardinality user properties.
   *
   * @param internalUserID - Platform internal user id.
   * @param properties - Low-cardinality user properties.
   */
  public SetUser(internalUserID: string, properties: TelemetryParams = {}): void {
    if (this.disabled) {
      Logger.Debug(localConstants.logContext, 'Telemetry disabled; skipping user assignment.');
      return;
    }
    if (!this.analytics) {
      Logger.Debug(localConstants.logContext, 'Telemetry not initialized; skipping user assignment.');
      return;
    }

    try {
      const safeUser = this.sanitize({ [localConstants.userIDKey]: internalUserID })[localConstants.userIDKey];
      if (typeof safeUser !== 'string' || !safeUser) {
        Logger.Debug(localConstants.logContext, 'Telemetry user id failed safety checks; skipping user assignment.');
        return;
      }
      const safeProperties = this.sanitize(this.withRuntimeContext(properties));
      setUserId(this.analytics, safeUser);
      setUserProperties(this.analytics, safeProperties);
    } catch (err: unknown) {
      this.analytics = null;
      Logger.Warn(localConstants.logContext, `Telemetry user assignment failed: ${Logger.GetErrorMessage(err)}`);
    }
  }

  /**
   * Fire-and-forget GA4 event logging.
   *
   * @param eventName - GA4 snake_case event name.
   * @param params - Flat scalar event params.
   */
  public Track(eventName: string, params: TelemetryParams = {}): void {
    if (this.disabled) {
      Logger.Debug(localConstants.logContext, `Telemetry disabled; skipping event ${eventName}.`);
      return;
    }
    if (!this.analytics) {
      if (this.initPromise) {
        this.pendingEvents.push({ eventName, params });
        Logger.Debug(localConstants.logContext, `Telemetry initializing; queueing event ${eventName}.`);
      } else {
        Logger.Debug(localConstants.logContext, `Telemetry not initialized; skipping event ${eventName}.`);
      }
      return;
    }

    try {
      const safeParams = this.sanitize(this.withRuntimeContext(params));
      logEvent(this.analytics, eventName, safeParams);
      Logger.Debug(localConstants.logContext, `Telemetry sent event ${eventName}: ${JSON.stringify(safeParams)}`);
    } catch (err: unknown) {
      this.analytics = null;
      Logger.Warn(localConstants.logContext, `Telemetry event failed: ${Logger.GetErrorMessage(err)}`);
    }
  }

  /** @returns The Firebase config shared with Auth, with GA4 measurement id added. */
  private getFirebaseOptions(): FirebaseOptions {
    return {
      apiKey: Settings.AUTH_API_KEY,
      authDomain: Settings.AUTH_DOMAIN,
      projectId: Settings.AUTH_PROJECT_ID,
      appId: Settings.AUTH_APP_ID,
      measurementId: Settings.AUTH_MEASUREMENT_ID
    };
  }

  /** @returns The existing Firebase app, or a newly initialized one when Auth has not run yet. */
  private getOrCreateFirebaseApp(): FirebaseApp {
    return getApps()[0] ?? initializeApp(this.getFirebaseOptions());
  }

  /** Initialize Firebase Analytics behind the public crash-safe Init wrapper. */
  private async initInternal(): Promise<void> {
    try {
      if (!Settings.AUTH_MEASUREMENT_ID) {
        this.pendingEvents = [];
        Logger.Debug(localConstants.logContext, 'GA4 measurement id is empty; telemetry remains disabled.');
        return;
      }

      const supported = await isSupported().catch(() => false);
      if (!supported) {
        this.pendingEvents = [];
        Logger.Debug(localConstants.logContext, 'Firebase Analytics is unsupported in this runtime; telemetry remains disabled.');
        return;
      }

      this.analytics = getAnalytics(this.getOrCreateFirebaseApp());
    } catch (err: unknown) {
      this.analytics = null;
      this.pendingEvents = [];
      Logger.Warn(localConstants.logContext, `Telemetry initialization failed: ${Logger.GetErrorMessage(err)}`);
    }
  }

  /** Sends any events that were tracked while initialization was still in flight. Called once after Init resolves successfully. */
  private flushPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;
    if (!this.analytics) {
      this.pendingEvents = [];
      return;
    }
    const queue = this.pendingEvents;
    this.pendingEvents = [];
    for (const { eventName, params } of queue) {
      if (!this.analytics) break;
      this.Track(eventName, params);
    }
  }

  /**
   * Attach adapter-owned runtime context after caller params so central values always win.
   *
   * @param params - Caller-provided params.
   * @returns Params with adapter-owned environment and version tags.
   */
  private withRuntimeContext(params: TelemetryParams): TelemetryParams {
    const merged: TelemetryParams = { ...params };
    delete merged[localConstants.runtimeEnvironmentKey];
    delete merged[localConstants.runtimeEnvModeKey];
    delete merged[localConstants.runtimeVersionKey];
    return {
      ...merged,
      [localConstants.runtimeEnvironmentKey]: Settings.ENV_MODE,
      [localConstants.runtimeVersionKey]: Settings.VERSION
    };
  }

  /**
   * Remove keys and values that look like PII or secrets. Returns a fresh safe bag.
   *
   * @param params - Unsanitized flat scalar params.
   * @returns Sanitized params.
   */
  private sanitize(params: TelemetryParams): TelemetryParams {
    const safe: TelemetryParams = {};
    for (const [key, value] of Object.entries(params)) {
      if (this.isForbiddenKey(key)) continue;
      if (typeof value === 'number') {
        if (Number.isFinite(value)) safe[key] = value;
        continue;
      }
      if (typeof value === 'string') {
        if (this.isForbiddenStringValue(value)) continue;
        safe[key] = value;
        continue;
      }
      safe[key] = value;
    }
    return safe;
  }

  /**
   * @param key - Param or user-property key.
   * @returns Whether the key is credential-bearing (token/password/secret/apikey) and must never reach analytics.
   */
  private isForbiddenKey(key: string): boolean {
    const normalized = key.toLowerCase();
    return localConstants.credentialKeyTokens.some((token) => normalized.includes(token));
  }

  /**
   * @param value - String param or user-property value.
   * @returns Whether the value is empty, over the length cap, or PII-shaped (email/SSN/phone/card) - used for our own event params so a code slip cannot leak PII into analytics.
   */
  private isForbiddenStringValue(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > localConstants.maxStringValueLength) return true;
    return localConstants.piiValuePatterns.some((pattern) => pattern.test(trimmed));
  }

  /**
   * @param value - Visitor URL query-param value.
   * @returns Whether the value is empty, over the length cap, or looks like an injection attempt (markup, dangerous scheme, control characters).
   */
  private isInjectionValue(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > localConstants.maxStringValueLength) return true;
    // Control characters are compared by code point (a regex control class trips no-control-regex).
    if ([...trimmed].some((ch) => ch.charCodeAt(0) < localConstants.controlCharCeiling)) return true;
    return localConstants.injectionValuePatterns.some((pattern) => pattern.test(trimmed));
  }
}
